import React from 'react';

import Axios from 'axios';
import { Formik, Field } from 'formik';
import { set } from 'lodash';
import * as Yup from 'yup';

import { datePickerToApi, getNow } from '@libs/NwMoment';
import { getMoment } from '@libs/NwMoment';

import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NWInput, NWTextAreaCharCounter, NWDatePicker } from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import { ProjectTransactionFormContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';

import AmountLine from './Common/AmountLine';
import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const CreditForm = ({ contactID, statementType, onCloseDrawer, onTransactionCreated, width, minWidth }) => {
    //onChangeForm
    useFormFieldFocus('Title');
    const transactionProperties = useTransactionLabels('Credit');
    const configurations = useConfigurations();

    const saveTransaction = async (values, setSubmitting) => {
        let apiData = {
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            Description: values.Description,
            Quantity: 1,
            TypeQuantity: 'Units',
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
            RefDate: values.RefDate && datePickerToApi(values.RefDate),
            Contacts: [
                {
                    ContactID: contactID,
                    ContactBillingInfoID: null,
                },
            ],
        };
        apiData.StatementType = statementType;

        try {
            await Axios.post('/transactions/credit', apiData);
            onTransactionCreated();
            onCloseDrawer();
            setSubmitting(false);
        } catch (error) {
            console.error('Create Credit Error => ', error);
            setSubmitting(false);
        }
    };

    const initialValues = {
        TransactionLabelID: transactionProperties.startLabel.ID,
        Title: transactionProperties.startLabel.Label,
        Description: '',
        Amount: [configurations.defaultCurrency, null],
        RefDate: getNow(),
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
            })}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid }) => (
                <NwForm
                    values={values}
                    //onTouchForm={onChangeForm}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <ProjectTransactionFormContainer>
                        <TransactionLabelsSelector
                            values={values}
                            setFieldValue={setFieldValue}
                            transactionProperties={transactionProperties}
                            updateTitle
                        />
                        <Field
                            component={NWDatePicker}
                            label="Date"
                            name="RefDate"
                            format="MMM DD, YYYY"
                            allowClear={false}
                            value={values.RefDate}
                        />
                        <Field
                            component={NWInput}
                            label="Title"
                            name="Title"
                            type="text"
                            value={values.Title}
                            maxLength={255}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Description"
                            name="Description"
                            type="text"
                            value={values.Description}
                            maxLength={2500}
                            autoSize={{ minRows: 3, maxRows: 10 }}
                        />
                        <AmountLine values={values} touched={touched} amountLabel="Credit Amount" hideQuantity />
                    </ProjectTransactionFormContainer>
                    <NwFixedCancelAndSaveBar
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onCancel={onCloseDrawer}
                        width={width}
                        minWidth={minWidth}
                    />
                </NwForm>
            )}
        </Formik>
    );
};

export default CreditForm;
